import React, { useState, useEffect } from 'react';
import { api } from '../config/api';
import { FiUserPlus, FiEdit2, FiTrash2, FiKey, FiEdit, FiMail, FiX } from 'react-icons/fi';
import { safeFormatDate } from '../utils/dateUtils';
import toast from 'react-hot-toast';
import { tw, monoFont } from '../utils/terminalTheme';

interface User {
  id: number;
  email: string;
  full_name: string | null;
  role: 'ADMIN' | 'VIEWER';
  is_active: boolean;
  created_at: string;
  invitation_accepted: boolean;
  last_login: string | null;
}

interface InviteFormData {
  email: string;
  full_name: string;
  role: 'ADMIN' | 'VIEWER';
}

export const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [inviteForm, setInviteForm] = useState<InviteFormData>({
    email: '',
    full_name: '',
    role: 'ADMIN'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get<User[]>('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const inviteUser = async () => {
    try {
      await api.post('/admin/users/invite', inviteForm);
      toast.success('User invited successfully! They will receive an email with login credentials.');
      setShowInviteForm(false);
      setInviteForm({ email: '', full_name: '', role: 'ADMIN' });
      loadUsers();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to invite user';
      toast.error(message);
    }
  };

  const updateUserRole = async (userId: number, role: 'ADMIN' | 'VIEWER') => {
    try {
      await api.put(`/admin/users/${userId}`, { role });
      toast.success('User role updated');
      loadUsers();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update user';
      toast.error(message);
    }
  };

  const toggleUserStatus = async (userId: number, isActive: boolean) => {
    try {
      await api.put(`/admin/users/${userId}`, { is_active: isActive });
      toast.success(`User ${isActive ? 'activated' : 'deactivated'}`);
      loadUsers();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update user';
      toast.error(message);
    }
  };

  const resetPassword = async (userId: number, userEmail: string) => {
    if (!window.confirm(`Reset password for ${userEmail}? They will receive a new temporary password via email.`)) {
      return;
    }

    try {
      await api.post(`/admin/users/${userId}/reset-password`);
      toast.success('Password reset successfully! User will receive an email.');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const resendInvitation = async (userId: number, userEmail: string) => {
    if (!window.confirm(`Resend invitation to ${userEmail}?`)) {
      return;
    }

    try {
      await api.post(`/admin/users/${userId}/resend-invitation`);
      toast.success('Invitation resent successfully!');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to resend invitation';
      toast.error(message);
    }
  };

  const deleteUser = async (userId: number, userEmail: string) => {
    if (!window.confirm(`Are you sure you want to delete ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to delete user';
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
            <span className="text-cyan-400">~/</span>admin/<span className="text-cyan-400">users</span>
            <span className="text-gray-600 text-sm font-normal ml-3">
              <span className="text-green-500">$</span> manage --users
            </span>
          </h1>
          <p className="text-gray-500 text-sm font-mono mt-1">
            {users.length} registered users
          </p>
        </div>
        <button
          onClick={() => setShowInviteForm(true)}
          className={tw.btnPrimary + ' flex items-center gap-2'}
        >
          <FiUserPlus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      {/* User Table */}
      <div className={tw.tableWrap}>
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-700/30 bg-gray-900/30">
              <th className={tw.tableHeader}>User</th>
              <th className={tw.tableHeader}>Role</th>
              <th className={tw.tableHeader}>Status</th>
              <th className={tw.tableHeader}>Invitation</th>
              <th className={tw.tableHeader}>Last Login</th>
              <th className={tw.tableHeaderRight}>Actions</th>
            </tr>
          </thead>
          <tbody className={tw.tableDivider}>
            {users.map((user) => (
              <tr key={user.id} className={tw.tableRow}>
                <td className={tw.tableCell}>
                  <div>
                    <div className="text-sm font-mono font-medium text-white">
                      {user.full_name || 'No name'}
                    </div>
                    <div className="text-[10px] font-mono text-gray-500">{user.email}</div>
                  </div>
                </td>
                <td className={tw.tableCell}>
                  {editingUser === user.id ? (
                    <select
                      value={user.role}
                      onChange={(e) => {
                        updateUserRole(user.id, e.target.value as 'ADMIN' | 'VIEWER');
                        setEditingUser(null);
                      }}
                      onBlur={() => setEditingUser(null)}
                      className={tw.select + ' w-auto'}
                      autoFocus
                    >
                      <option value="ADMIN">Admin</option>
                      <option value="VIEWER">Viewer</option>
                    </select>
                  ) : (
                    <span
                      onClick={() => setEditingUser(user.id)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono border cursor-pointer hover:opacity-80 transition-opacity ${
                        user.role === 'ADMIN'
                          ? 'bg-purple-500/15 text-purple-400 border-purple-500/30'
                          : 'bg-gray-500/15 text-gray-400 border-gray-500/30'
                      }`}
                      title="Click to edit role"
                    >
                      {user.role}
                      <FiEdit className="w-2.5 h-2.5" />
                    </span>
                  )}
                </td>
                <td className={tw.tableCell}>
                  <button
                    onClick={() => toggleUserStatus(user.id, !user.is_active)}
                    className={`inline-flex px-2 py-0.5 rounded text-[10px] font-mono border transition-colors ${
                      user.is_active
                        ? 'bg-green-500/15 text-green-400 border-green-500/30 hover:bg-green-500/25'
                        : 'bg-red-500/15 text-red-400 border-red-500/30 hover:bg-red-500/25'
                    }`}
                  >
                    {user.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className={tw.tableCell}>
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-mono border ${
                    user.invitation_accepted
                      ? 'bg-green-500/15 text-green-400 border-green-500/30'
                      : 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                  }`}>
                    {user.invitation_accepted ? 'Accepted' : 'Pending'}
                  </span>
                </td>
                <td className={tw.tableCell}>
                  <span className="text-xs text-gray-500 font-mono">
                    {user.last_login ? safeFormatDate(user.last_login, 'MMM d, yyyy h:mm a') : 'Never'}
                  </span>
                </td>
                <td className={tw.tableCellRight}>
                  <div className="flex justify-end gap-2">
                    {!user.invitation_accepted && (
                      <button
                        onClick={() => resendInvitation(user.id, user.email)}
                        className="text-cyan-400 hover:text-cyan-300 transition-colors"
                        title="Resend invitation"
                      >
                        <FiMail className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => resetPassword(user.id, user.email)}
                      className="text-amber-400 hover:text-amber-300 transition-colors"
                      title="Reset password"
                    >
                      <FiKey className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => deleteUser(user.id, user.email)}
                      className="text-gray-500 hover:text-red-400 transition-colors"
                      title="Delete user"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className={tw.formWrap + ' w-full max-w-md'}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-mono text-cyan-400">Invite New User</h2>
              <button
                onClick={() => {
                  setShowInviteForm(false);
                  setInviteForm({ email: '', full_name: '', role: 'ADMIN' });
                }}
                className="text-gray-500 hover:text-white transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg mb-4">
              <p className="text-xs text-cyan-400/80 font-mono">
                The user will receive a welcome email with login credentials and instructions.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className={tw.label}>Email</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className={tw.input}
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className={tw.label}>Full Name</label>
                <input
                  type="text"
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                  className={tw.input}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className={tw.label}>Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as 'ADMIN' | 'VIEWER' })}
                  className={tw.select}
                >
                  <option value="ADMIN">Admin</option>
                  <option value="VIEWER">Viewer</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteForm(false);
                  setInviteForm({ email: '', full_name: '', role: 'ADMIN' });
                }}
                className={tw.btnSecondary}
              >
                Cancel
              </button>
              <button
                onClick={inviteUser}
                disabled={!inviteForm.email || !inviteForm.full_name}
                className={tw.btnPrimary + ' disabled:opacity-50 disabled:cursor-not-allowed'}
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
